import { useCartManagement } from "@/hooks/useCartManagement";
import { useProductManagement } from "@/hooks/useProductManagement";
import { useButtonVisibility } from "@/hooks/useButtonVisibility";
import { ButtonVisibilityToggle } from "./ButtonVisibilityToggle";
import { StockHeader } from "./StockHeader";
import { DialogManager } from "./dialog/DialogManager";
import { InventoryManager } from "./inventory/InventoryManager";
import { useStockDialogs } from "./hooks/useStockDialogs";
import { useInventoryState } from "./hooks/useInventoryState";

interface StockContentProps {
  stockItems: any[];
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  refetch: () => void;
  onInventoryStateChange: (isActive: boolean) => void;
}

export const StockContent = ({
  stockItems,
  searchQuery,
  setSearchQuery,
  selectedCategory,
  setSelectedCategory,
  refetch,
  onInventoryStateChange,
}: StockContentProps) => {
  // Custom hooks
  const { buttonVisibility, handleToggleVisibility } = useButtonVisibility();
  const {
    isCartDialogOpen,
    setIsCartDialogOpen,
    handleAddToCart
  } = useCartManagement(refetch);
  const {
    isAddProductDialogOpen,
    setIsAddProductDialogOpen,
    isDeleteProductDialogOpen,
    setIsDeleteProductDialogOpen,
    handleAddProduct,
    handleDeleteProduct,
  } = useProductManagement(refetch);
  const {
    isRestockDialogOpen,
    setIsRestockDialogOpen,
    isLossDialogOpen,
    setIsLossDialogOpen,
    isReturnDialogOpen,
    setIsReturnDialogOpen,
    selectedItem,
    handleRestockClick,
    handleReturnClick,
    handleLossClick,
  } = useStockDialogs();
  const {
    isInventoryActive,
    setIsInventoryActive,
    handleInventoryComplete,
  } = useInventoryState(onInventoryStateChange);

  return (
    <div className="relative">
      <ButtonVisibilityToggle
        buttonTypes={[
          { id: 'cart', label: 'Bouton Panier', isVisible: buttonVisibility.cart },
          { id: 'restock', label: 'Bouton Réapprovisionnement', isVisible: buttonVisibility.restock },
          { id: 'return', label: 'Bouton Retour', isVisible: buttonVisibility.return },
          { id: 'quantity', label: 'Champ Quantité', isVisible: buttonVisibility.quantity },
          { id: 'actions', label: 'Autres Actions', isVisible: buttonVisibility.actions },
          { id: 'inventory', label: 'Bouton Inventaire', isVisible: buttonVisibility.inventory },
          { id: 'loss', label: 'Bouton Perte', isVisible: buttonVisibility.loss },
          { id: 'delete', label: 'Bouton Supprimer', isVisible: buttonVisibility.delete },
          { id: 'add', label: 'Bouton Ajouter', isVisible: buttonVisibility.add },
        ]}
        onToggle={handleToggleVisibility}
      />

      <StockHeader
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        onOpenAddDialog={() => setIsAddProductDialogOpen(true)}
        onOpenDeleteDialog={() => setIsDeleteProductDialogOpen(true)}
        onOpenCart={() => setIsCartDialogOpen(true)}
        onOpenLossDialog={handleLossClick}
        stockItems={stockItems}
        onRestockClick={handleRestockClick}
        refetch={refetch}
        onInventoryStart={() => setIsInventoryActive(true)}
        onInventoryComplete={handleInventoryComplete}
        isInventoryActive={isInventoryActive}
        buttonVisibility={buttonVisibility}
      />

      <InventoryManager
        isInventoryActive={isInventoryActive}
        stockItems={stockItems}
        buttonVisibility={buttonVisibility}
        refetch={refetch}
        onRestockClick={handleRestockClick}
        onReturnClick={handleReturnClick}
      />

      <DialogManager
        isAddProductDialogOpen={isAddProductDialogOpen}
        setIsAddProductDialogOpen={setIsAddProductDialogOpen}
        isDeleteProductDialogOpen={isDeleteProductDialogOpen}
        setIsDeleteProductDialogOpen={setIsDeleteProductDialogOpen}
        isCartDialogOpen={isCartDialogOpen}
        setIsCartDialogOpen={setIsCartDialogOpen}
        isRestockDialogOpen={isRestockDialogOpen}
        setIsRestockDialogOpen={setIsRestockDialogOpen}
        isReturnDialogOpen={isReturnDialogOpen}
        setIsReturnDialogOpen={setIsReturnDialogOpen}
        isLossDialogOpen={isLossDialogOpen}
        setIsLossDialogOpen={setIsLossDialogOpen}
        selectedItem={selectedItem}
        stockItems={stockItems}
        handleAddProduct={handleAddProduct}
        handleDeleteProduct={handleDeleteProduct}
        refetch={refetch}
      />
    </div>
  );
};